import { getElementAnimations } from "../types";

import {
  definePreset,
  DIRECTION_SCHEMA,
  rad,
  setStatic,
  SLIDE_VECTORS,
  timing,
} from "./helpers";

import type { AnimationPreset, ParamSchema } from "./types";

/** Start scale for presets that grow into place. */
const FROM_SCALE_SCHEMA: ParamSchema = {
  key: "from",
  label: "From scale",
  min: 0,
  max: 1,
  step: 0.05,
};

/**
 * Building-block templates: the four base transforms as free-form clips plus
 * the cross-element cascade. Each primitive tweens an additive/multiplicative
 * channel, so stacking them as separate clips on one element composes into a
 * full transform animation (dx/dy and rotation sum, scale and opacity
 * multiply). Speed/frequency live on the clip: duration, easing, spring,
 * repeat, yoyo, repeat-delay.
 *
 * The 2026-08 cleanup removed the one-off scene gimmicks (paperWriting,
 * newspaperHighlight, timerCountdown, …) — every one was an existing preset
 * (or a stack of two) under a new name. Legacy scene clips still play via
 * LEGACY_PRESET_ALIASES in ../types.ts.
 */
export const templatePresets: AnimationPreset[] = [
  definePreset(
    "translateBy",
    "Translate",
    "templates",
    {
      params: { x: 200, y: 0 },
      schema: [
        { key: "x", label: "Offset X", min: -1000, max: 1000, step: 10 },
        { key: "y", label: "Offset Y", min: -1000, max: 1000, step: 10 },
      ],
      duration: 1500,
      easing: "power2.inOut",
    },
    (tl, target, _element, cfg) => {
      tl.to(
        target,
        { dx: cfg.params.x ?? 200, dy: cfg.params.y ?? 0, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "rotateBy",
    "Rotate",
    "templates",
    {
      params: { fromDegrees: 0, degrees: 360, pivotX: 0.5, pivotY: 0.5 },
      schema: [
        // degrees (not turns) so small precise sweeps work — e.g. a −10°→+10°
        // pendulum is fromDegrees −10, degrees 10, yoyo on
        { key: "fromDegrees", label: "From °", min: -1440, max: 1440, step: 5 },
        { key: "degrees", label: "To °", min: -1440, max: 1440, step: 5 },
        // beyond-bbox range so the anchor can sit outside the shape (orbit
        // around a nearby point); widening is backward-safe, narrowing would
        // drop saved clips via validateParamBounds
        { key: "pivotX", label: "Pivot X", min: -1, max: 2, step: 0.05 },
        { key: "pivotY", label: "Pivot Y", min: -1, max: 2, step: 0.05 },
      ],
      duration: 1500,
      easing: "power2.inOut",
    },
    // negative degrees spin counter-clockwise; pivot 0/0 is the top-left
    // corner, 1/1 the bottom-right, 0.5/0.5 the center — set by dragging the
    // on-canvas anchor handle or the "Pick on canvas" button (anchorPick.ts)
    (tl, target, _element, cfg) => {
      setStatic(tl, target, {
        anchorX: cfg.params.pivotX ?? 0.5,
        anchorY: cfg.params.pivotY ?? 0.5,
      });
      // legacy clips (pre-2026-08-12) persisted whole `turns` instead
      const degrees = cfg.params.degrees ?? (cfg.params.turns ?? 1) * 360;
      tl.fromTo(
        target,
        { angleDelta: rad(cfg.params.fromDegrees ?? 0) },
        { angleDelta: rad(degrees), ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "rollBy",
    "Roll",
    "templates",
    {
      params: { turns: 1 },
      schema: [{ key: "turns", label: "Turns", min: -4, max: 4, step: 0.25 }],
      duration: 1500,
      easing: "power2.inOut",
    },
    // wheel roll: rotation about the center coupled with the matching ground
    // travel (dx = θ·r, r = half the larger bbox side). Positive turns roll
    // right, negative roll left — physically consistent for both.
    (tl, target, element, cfg) => {
      const theta = rad((cfg.params.turns ?? 1) * 360);
      // ponytail: preview builds pass a zero-sized dummy element — fall back
      // to a visible radius so the gallery tile still travels
      const r = Math.max(element.width, element.height, 40) / 2;
      tl.to(target, { angleDelta: theta, dx: theta * r, ...timing(cfg) }, 0);
    },
  ),
  definePreset(
    "scaleTo",
    "Scale",
    "templates",
    {
      params: { x: 1.5, y: 1.5, pivotX: 0.5, pivotY: 0.5 },
      schema: [
        { key: "x", label: "Scale X", min: 0, max: 4, step: 0.05 },
        { key: "y", label: "Scale Y", min: 0, max: 4, step: 0.05 },
        { key: "pivotX", label: "Pivot X", min: 0, max: 1, step: 0.05 },
        { key: "pivotY", label: "Pivot Y", min: 0, max: 1, step: 0.05 },
      ],
      duration: 1500,
      easing: "power2.inOut",
    },
    // independent axes: equal x/y is a zoom, unequal is a stretch
    (tl, target, _element, cfg) => {
      setStatic(tl, target, {
        anchorX: cfg.params.pivotX ?? 0.5,
        anchorY: cfg.params.pivotY ?? 0.5,
      });
      tl.to(
        target,
        {
          scaleX: cfg.params.x ?? 1.5,
          scaleY: cfg.params.y ?? 1.5,
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "opacityTo",
    "Opacity",
    "templates",
    {
      params: { from: 100, to: 30 },
      schema: [
        { key: "from", label: "From %", min: 0, max: 100, step: 5 },
        { key: "to", label: "To %", min: 0, max: 100, step: 5 },
      ],
      duration: 1500,
      easing: "power2.inOut",
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { opacity: (cfg.params.from ?? 100) / 100 },
        { opacity: (cfg.params.to ?? 30) / 100, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "staggeredEntrance",
    "Staggered Entrance",
    "templates",
    {
      params: { stagger: 0.15, direction: 3, distance: 60, from: 0.5 },
      schema: [
        { key: "stagger", label: "Stagger s", min: 0, max: 1, step: 0.05 },
        DIRECTION_SCHEMA,
        { key: "distance", label: "Distance px", min: 0, max: 500, step: 10 },
        FROM_SCALE_SCHEMA,
      ],
      duration: 2000,
      easing: "back.out",
    },
    (tl, target, element, cfg, sceneElements) => {
      // cascade across every element carrying this preset, in scene (z) order
      const order = (sceneElements ?? []).filter(
        (el) =>
          !el.isDeleted &&
          getElementAnimations(el).some(
            (clip) => clip.preset === "staggeredEntrance",
          ),
      );
      const index = Math.max(
        0,
        order.findIndex((el) => el.id === element.id),
      );
      const [vx, vy] =
        SLIDE_VECTORS[cfg.params.direction ?? 3] ?? SLIDE_VECTORS[3];
      const d = cfg.params.distance ?? 60;
      const from = cfg.params.from ?? 0.5;
      tl.fromTo(
        target,
        { opacity: 0, scaleX: from, scaleY: from, dx: vx * d, dy: vy * d },
        {
          opacity: 1,
          scaleX: 1,
          scaleY: 1,
          dx: 0,
          dy: 0,
          ...timing(cfg),
          delay: cfg.delay / 1000 + index * (cfg.params.stagger ?? 0.15),
        },
        0,
      );
    },
  ),
];
